import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AnimationOptions } from 'ngx-lottie';
import * as Highcharts from 'highcharts';
import * as moment from 'moment';
import { MatDialog } from '@angular/material/dialog';
import { MapDialogComponent } from '../map-dialog/map-dialog.component';
import { DataService } from 'src/app/core/services/data-service.service';
import { createChart, getConversionRate, getMappedData, getWeatherStateIcon, replaceUnitPlaceholder } from 'src/app/helperModules/chart.helpers';
import { IChartValue, ISerie, IWidget, IndexFrontend } from 'src/app/shared/models/seriesInfo.interfaces';
import { _MAT_HINT } from '@angular/material/form-field';
import { Subscription } from 'rxjs';

export interface IWeather {
  weatherState: number;
  maxTemp: number;
  minTemp: number;
  windDir: number;
  windSpeed: number;
}

@Component({
  selector: 'app-wind-atmosphere',
  templateUrl: './wind-atmosphere.component.html',
  styleUrls: ['./wind-atmosphere.component.scss'],
})
export class WindAtmosphereComponent implements OnInit, OnDestroy {
  flipped: boolean = false;
  baseWindUnit: string;
  baseWindUnitChart: string;
  selectedWindUnit: string;
  isLastValuesModeled: boolean;

  options: AnimationOptions = {
    path: 'assets/json/windmill.json',
  };

  styles: Partial<CSSStyleDeclaration> = {
    maxWidth: '500px',
    margin: '-50px 0 0 20px',
  };
  
  lastValuesDate: Date;
  weatherValues: IWeather[] = [];
  
  parameterColumns = [
    { index: 1.7, title: 'Intensidade do vento', icon: 'fas fa-wind', isWind: true },
    { index: 1.3, title: 'Precipitação', icon: 'fad fa-cloud-rain' },
    { index: 1.6, title: 'Rajada do vento', icon: 'fad fa-wind', isWind: true },
    { index: 1.4, title: 'Humidade relativa', icon: 'fas fa-humidity' },
    { index: 1.5, title: 'Direção do vento', icon: 'fas fa-location-circle', extraPipe: 'directionCompassRose' },
    { index: 1.2, title: 'Pressão atmosférica', icon: 'fas fa-tachometer-alt-fast' },
    { index: 1.16, title: 'Visibilidade', icon: 'fad fa-smog' },
  ];

  parameterRows: any[] = [];

  chart: Highcharts.Chart;
  createChart;
  chartSettings;
  seriesSettings = [];
  
  dataSubscription: Subscription;
  widgetData: IWidget;
  mappedData: Map<IndexFrontend, ISerie[]> = new Map<IndexFrontend, ISerie[]>();
  toleranceStatusMap: Record<string, boolean> = {};

  constructor(
    private dataService: DataService, 
    private dialog: MatDialog, 
    private cdRef: ChangeDetectorRef 
  ) {}

  ngOnInit(): void {
    this.dataService.getChartSettings().subscribe((settings: any) => {
      this.chartSettings = settings;
    });
    
    this.dataSubscription = this.dataService.windAtmosphereData$.subscribe(widget => {
      if (widget.charts.yAxis.length > 0 && widget.lastValues.length > 0) {
        this.widgetData = widget;
        this.mappedData = getMappedData(this.widgetData);
        this.updateToleranceStatus();
        
        this.selectedWindUnit = localStorage.getItem('velocityUnit') || this.dataService.initialVelocityUnit;
        this.baseWindUnit = this.mappedData.get(IndexFrontend.WindIntensityLastValue)[0].unit;
        this.baseWindUnitChart = this.mappedData.get(IndexFrontend.WindIntensityChartAndTable)[0].unit

        this.isLastValuesModeled = this.dataService.isWindAtmosphereModeled;
        this.lastValuesDate = this.widgetData.lastValues[0].date;

        this.createWeatherTable();
        this.drawChart();
        if (this.selectedWindUnit !== this.baseWindUnit && this.selectedWindUnit !== this.baseWindUnitChart) {
          this.windUnitChanged(this.selectedWindUnit);
        }
        this.updateVisibleRows(2);
      }
    })
  }

  ngOnDestroy(): void { this.dataSubscription?.unsubscribe(); }

  drawChart() {
    this.createChart = createChart.bind(this);
    this.createChart(this.widgetData.charts, this.seriesSettings, this.chartSettings, 'nós', 'm/s');

    if (this.seriesSettings.length > 0) {
      this.chartSettings.series = this.seriesSettings;
      if (this.chart === undefined) {
        this.chart = Highcharts.chart('windChart', this.chartSettings);
      }
      else {
        this.chart.update(this.chartSettings);
      }
    }
  }

  getDifferenceBetweenDateValues(values: IChartValue[], daysAhead: number) {
    let targetDate = moment().add(daysAhead, 'days').startOf('day');
    let noonTime = targetDate.clone().add(12, 'hours').toDate().getTime();

    // Filter to include only the values from the specific day (starting from midnight to midnight next day)
    return values
      .filter(v => {
        let vDate = new Date(v.date).getTime();
        return vDate >= targetDate.toDate().getTime() && vDate < targetDate.clone().add(1, 'day').toDate().getTime();
      })
      .map(v => ({
        difference: Math.abs(noonTime - new Date(v.date).getTime()),
        value: v.value,
        date: v.date
      }));
  }

  createWeatherTable() {
    this.weatherValues = [];
    let temperatureValues = this.mappedData.get(IndexFrontend.TemperatureChart).filter(s => !s.isMeasure)[0];
    let windDirectionValues = this.mappedData.get(IndexFrontend.WindDirectionTable).filter(s => !s.isMeasure)[0];
    let windIntensityValues = this.mappedData.get(IndexFrontend.WindIntensityChartAndTable).filter(s => !s.isMeasure)[0];
    let weatherStateValues = this.mappedData.get(IndexFrontend.WeatherStateTable).filter(s => !s.isMeasure)[0];

    for (let day = 0; day < 5; day++) {
      // Filter values to find those closest to noon
      const getClosestValueToNoon = (values: IChartValue[]) => {
        let noonValues = this.getDifferenceBetweenDateValues(values, day);
        if (noonValues.length === 0) return null;
        return noonValues.reduce((a, b) => a.difference < b.difference ? a : b).value;
      };

      let temperatureNoonValues = this.getDifferenceBetweenDateValues(temperatureValues.chartValues, day);
      let maxTemp = Math.max(...temperatureNoonValues.map(v => v.value));
      let minTemp = Math.min(...temperatureNoonValues.map(v => v.value));

      this.weatherValues.push({
        weatherState: getClosestValueToNoon(weatherStateValues.chartValues),
        maxTemp: maxTemp,
        minTemp: minTemp,
        windDir: getClosestValueToNoon(windDirectionValues.chartValues),
        windSpeed: getClosestValueToNoon(windIntensityValues.chartValues)
      });
    }
  }

  getWeatherStateIcon = (stateValue: number) => getWeatherStateIcon(stateValue);

  checkExists(indexFrontend: IndexFrontend) : boolean { 
    return this.mappedData.has(indexFrontend);
  }

  windUnitChanged(newUnit: string) : void {
    localStorage.setItem('velocityUnit', newUnit);
    if (this.baseWindUnit !== newUnit) {
      let convertConstant = getConversionRate(this.baseWindUnit, newUnit);

      // Converting last values
      this.mappedData.get(IndexFrontend.WindIntensityLastValue)[0].lastValue *= convertConstant;
      if (this.checkExists(IndexFrontend.WindGustLastValue)){
        this.mappedData.get(IndexFrontend.WindGustLastValue)[0].lastValue *= convertConstant;
      }
      this.mappedData.get(IndexFrontend.WindIntensityChartAndTable).filter(s => !s.isMeasure)[0]
        .chartValues.forEach((v : IChartValue) => { v.value *= convertConstant });
      
      this.createWeatherTable();
      this.updateChart(newUnit, convertConstant);
      this.updateVisibleRows(2);

      this.selectedWindUnit = newUnit;
      this.baseWindUnit = newUnit;
      this.baseWindUnitChart = newUnit;
    }
  }

  updateChart(newUnit: string, convertConstant: number) : void {
    // Update wind chart values
    let windChartSeries = this.mappedData.get(IndexFrontend.WindIntensityChartAndTable);
    windChartSeries.forEach(s => {
      this.chartSettings.series.forEach((serie: any) => {
        if (serie.id === s.id) {
          serie.data.forEach((d: any) => d[1] = parseFloat((d[1] * convertConstant).toFixed(2)));
          serie.tooltip = { valueSuffix: ` ${newUnit}` };
        }
      });
    });

    let yAxis = this.chartSettings.yAxis.find(y => y.unit === this.baseWindUnit || y.unit === this.baseWindUnitChart);
    if (yAxis) {
      // Convert min and max
      if (typeof yAxis.max === 'number') yAxis.max *= convertConstant;
      if (typeof yAxis.min === 'number') yAxis.min *= convertConstant;

      // Recalculate ticks if not dynamic
      if (yAxis.tickPositions) {
        const min = yAxis.min || 0;
        const max = yAxis.max || 0;
        const interval = yAxis.tickPositions[1] - yAxis.tickPositions[0];
        const newInterval = interval * convertConstant;

        let newTicks: number[] = [];
        let numTicks = Math.ceil((max - min) / newInterval);
        for (let i = 0; i <= numTicks; i++) {
          let tick = parseFloat((min + i * newInterval).toFixed(2));
          if (tick <= max) {
            newTicks.push(tick);
          }
        }
        if (newTicks[newTicks.length - 1] !== max) newTicks.push(parseFloat(max.toFixed(2)));

        yAxis.tickPositions = newTicks;
      } 
      yAxis.unit = newUnit;
      yAxis.title.text = replaceUnitPlaceholder(yAxis.title.text, newUnit, /\[(.*?)\]/);
    }
    
    this.chart.update(this.chartSettings);
  }

  openMap() {
    this.dialog.open(MapDialogComponent, {
      minWidth: '50%', maxWidth: '95%',
      minHeight: '50%', maxHeight: '95%',
      data: {
        allData: Array.from(this.mappedData.values()).reduce((acc, series) => acc.concat(series)),
      },
      panelClass: 'custom-dialog-container',
    });
  }

  updateToleranceStatus(): void {
    this.toleranceStatusMap = {};
  
    const flattenedAndFiltered = Array.from(this.mappedData.values())
      .reduce((acc, curr) => acc.concat(curr), [])
      .filter(serie => serie.lastValue !== undefined && serie.lastValue !== null);
  
    for (let serie of flattenedAndFiltered) {
      const tolerance = moment().subtract(serie.communicationTolerance ?? 86400, 'seconds');
      const isUnderTolerance = !moment(serie.date).isBefore(tolerance);
      this.toleranceStatusMap[serie.id] = isUnderTolerance;
    }
  }  

  isDataUnderTolerance() : boolean {
    return Object.values(this.toleranceStatusMap).every(status => status === true);
  }
  
  getRotation(indexFrontend: number): string {
    return this.checkExists(indexFrontend) != null 
      ? `${-45 + this.mappedData.get(indexFrontend)[0].lastValue + 180}deg` 
      : '0deg';
  }

  add180 = (windDirection: number) => -45 + windDirection + 180;

  isRowReducedWidth(row: any): boolean {
    return !row.columns.every((col: any) => this.checkExists(col.index));
  }

  updateVisibleRows(columnsPerRow: number): void {
    const visibleColumns = this.parameterColumns.filter(col => this.checkExists(col.index));
    this.parameterRows = [];
    for (let i = 0; i < visibleColumns.length; i += columnsPerRow) {
      this.parameterRows.push({
        columns: visibleColumns.slice(i, i + columnsPerRow),
      });
    }
  }
}
